import { useCallback } from 'react';

import type { AppEvent, AppEventName, AppEventProperties } from './events';
import { trackEvent } from './mixpanel';

type EmptyProps = Record<string, never>;

// Split event names into "needs a properties object" vs "no properties"
// so the overloads can require the second arg only when the variant's
// properties type is non-empty. The original `Exclude<…, AppEvent extends
// {…} ? N : never>` form distributed incorrectly and the no-arg overload
// silently swallowed events that should have required props.
type EventNamesWithEmptyProps = Extract<AppEvent, { properties: EmptyProps }>['name'];
type EventNamesWithProps = Exclude<AppEventName, EventNamesWithEmptyProps>;

type TrackFn = {
  <N extends EventNamesWithProps>(name: N, properties: AppEventProperties<N>): void;
  <N extends EventNamesWithEmptyProps>(name: N): void;
};

// Type-safe tracking. Pass an event name from `AppEventName` and the
// matching properties object. Adding a new event = add a new variant
// in `events.ts`; the call site then type-checks against it.
export function useAnalytics(): { track: TrackFn } {
  const track = useCallback(
    <N extends AppEventName>(name: N, properties?: AppEventProperties<N>) => {
      trackEvent(name, properties as Record<string, unknown> | undefined);
    },
    [],
  );

  return { track: track as TrackFn };
}
